import { AudioStreamer } from '../../lib/audio-streamer';
import { audioContext } from '../../lib/utils';
import { AUDIO_ANALYSIS_BAND_COUNT } from './audio-analysis';

export interface VoicePlaybackBackend {
  resume(deviceId?: string): Promise<void>;
  enqueue(pcm: Uint8Array, sampleRateHz: number): void;
  complete(): void;
  interrupt(): void;
  close(): Promise<void>;
}

interface AudioStreamerLike {
  setSinkId(deviceId: string): Promise<void>;
  resume(): Promise<void>;
  addPCM16(pcm: Uint8Array, sampleRateHz: number): void;
  stop(): void;
  complete(): void;
  release(): void;
}

interface VoicePlaybackOptions {
  onLevel: (level: number) => void;
  onSpectrum?: (spectrum: number[]) => void;
  createBackend?: (options: {
    onLevel: (level: number) => void;
    onSpectrum: (spectrum: number[]) => void;
  }) => VoicePlaybackBackend;
  createAudioElement?: () => HTMLAudioElement;
  acquireContext?: () => Promise<AudioContext>;
  createStreamer?: (
    context: AudioContext,
    onLevel: (level: number) => void,
    audioElement: HTMLAudioElement,
    onSpectrum: (spectrum: number[]) => void,
  ) => AudioStreamerLike;
}

const clampLevel = (level: number): number =>
  Number.isFinite(level) ? Math.max(0, Math.min(1, level)) : 0;

export class VoicePlayback {
  private readonly backend: VoicePlaybackBackend;
  private spectrumWasSilent = false;
  private readonly onSpectrum: (spectrum: number[]) => void;

  constructor(options: VoicePlaybackOptions) {
    const onLevel = (level: number) => options.onLevel(clampLevel(level));
    this.onSpectrum = (spectrum: number[]) => {
      const safeSpectrum = spectrum.map(clampLevel);
      this.spectrumWasSilent = safeSpectrum.every((value) => value === 0);
      options.onSpectrum?.(safeSpectrum);
    };
    this.backend = options.createBackend
      ? options.createBackend({ onLevel, onSpectrum: this.onSpectrum })
      : createAudioStreamerBackend({
          onLevel,
          onSpectrum: this.onSpectrum,
          createAudioElement: options.createAudioElement,
          acquireContext: options.acquireContext,
          createStreamer: options.createStreamer,
        });
  }

  async resume(deviceId?: string): Promise<void> {
    await this.backend.resume(deviceId || undefined);
    this.spectrumWasSilent = false;
  }

  enqueue(pcm: Uint8Array, sampleRateHz: number): void {
    if (!Number.isSafeInteger(sampleRateHz) || sampleRateHz <= 0) {
      throw new Error('Voice output sample rate must be a positive integer');
    }
    this.backend.enqueue(pcm, sampleRateHz);
  }

  interrupt(): void {
    this.backend.interrupt();
    this.publishSilentSpectrum();
  }

  complete(): void {
    this.backend.complete();
  }

  async close(): Promise<void> {
    try {
      await this.backend.close();
    } finally {
      this.publishSilentSpectrum();
    }
  }

  private publishSilentSpectrum(): void {
    if (this.spectrumWasSilent) return;
    this.onSpectrum(Array(AUDIO_ANALYSIS_BAND_COUNT).fill(0));
  }
}

class AudioStreamerBackend implements VoicePlaybackBackend {
  private context: AudioContext | null = null;
  private streamer: AudioStreamerLike | null = null;
  private outputElement: HTMLAudioElement | null = null;
  private disposed = false;
  private generation = 0;
  private readonly releasedStreamers = new WeakSet<AudioStreamerLike>();
  private readonly releasedContexts = new WeakSet<AudioContext>();

  constructor(
    private readonly onLevel: (level: number) => void,
    private readonly createAudioElement: () => HTMLAudioElement,
    private readonly acquireContext: () => Promise<AudioContext>,
    private readonly createStreamer: (
      context: AudioContext,
      onLevel: (level: number) => void,
      audioElement: HTMLAudioElement,
      onSpectrum: (spectrum: number[]) => void,
    ) => AudioStreamerLike,
    private readonly onSpectrum: (spectrum: number[]) => void,
  ) {}

  resume(deviceId?: string): Promise<void> {
    if (this.disposed)
      return Promise.reject(new Error('Voice playback is disposed'));
    const generation = ++this.generation;
    const outputElement = this.outputElement ?? this.createAudioElement();
    this.outputElement = outputElement;
    this.releaseActiveOwnership();
    const acquiringContext = this.acquireContext();
    return this.finishResume(
      acquiringContext,
      outputElement,
      generation,
      deviceId,
    );
  }

  private async finishResume(
    acquiringContext: Promise<AudioContext>,
    outputElement: HTMLAudioElement,
    generation: number,
    deviceId?: string,
  ): Promise<void> {
    const context = await acquiringContext;
    if (this.disposed || this.generation !== generation) {
      if (context.state !== 'closed') await context.close();
      throw new Error('Voice playback acquisition was disposed');
    }
    this.context = context;
    let streamer: AudioStreamerLike | null = null;
    try {
      const onSpectrum = (spectrum: number[]): void => {
        if (
          !this.disposed &&
          this.generation === generation &&
          this.streamer === streamer
        ) {
          this.onSpectrum(spectrum);
        }
      };
      streamer = this.createStreamer(
        context,
        this.onLevel,
        outputElement,
        onSpectrum,
      );
      this.streamer = streamer;
      if (deviceId) await streamer.setSinkId(deviceId);
      this.assertActive(generation);
      await streamer.resume();
      this.assertActive(generation);
    } catch (acquisitionError: unknown) {
      await this.releaseAcquisition(context, streamer);
      throw acquisitionError;
    }
  }

  private async releaseAcquisition(
    context: AudioContext | null,
    streamer: AudioStreamerLike | null,
  ): Promise<void> {
    if (this.streamer === streamer) this.streamer = null;
    if (this.context === context) this.context = null;
    const attempts: Array<() => void | Promise<void>> = [];
    if (streamer && !this.releasedStreamers.has(streamer)) {
      this.releasedStreamers.add(streamer);
      attempts.push(
        () => streamer.stop(),
        () => streamer.release(),
      );
    }
    if (
      context &&
      context.state !== 'closed' &&
      !this.releasedContexts.has(context)
    ) {
      this.releasedContexts.add(context);
      attempts.push(() => context.close());
    }
    await Promise.allSettled(
      attempts.map((attempt) => {
        try {
          return Promise.resolve(attempt());
        } catch (cleanupError: unknown) {
          return Promise.reject(cleanupError);
        }
      }),
    );
  }

  private releaseActiveOwnership(): void {
    const context = this.context;
    const streamer = this.streamer;
    this.context = null;
    this.streamer = null;
    if (context || streamer) void this.releaseAcquisition(context, streamer);
  }

  private assertActive(generation: number): void {
    if (this.disposed || this.generation !== generation) {
      throw new Error('Voice playback acquisition was disposed');
    }
  }

  enqueue(pcm: Uint8Array, sampleRateHz: number): void {
    if (!this.streamer) {
      throw new Error('Voice playback requires a user gesture before audio');
    }
    this.streamer.addPCM16(pcm, sampleRateHz);
  }

  interrupt(): void {
    this.streamer?.stop();
    this.onLevel(0);
  }

  complete(): void {
    this.streamer?.complete();
  }

  async close(): Promise<void> {
    this.disposed = true;
    this.generation += 1;
    this.outputElement?.pause();
    this.outputElement = null;
    const context = this.context;
    const streamer = this.streamer;
    this.context = null;
    this.streamer = null;
    await this.releaseAcquisition(context, streamer);
  }
}

function createAudioStreamerBackend(options: {
  onLevel: (level: number) => void;
  onSpectrum: (spectrum: number[]) => void;
  createAudioElement?: () => HTMLAudioElement;
  acquireContext?: () => Promise<AudioContext>;
  createStreamer?: (
    context: AudioContext,
    onLevel: (level: number) => void,
    audioElement: HTMLAudioElement,
    onSpectrum: (spectrum: number[]) => void,
  ) => AudioStreamerLike;
}): VoicePlaybackBackend {
  return new AudioStreamerBackend(
    options.onLevel,
    options.createAudioElement ?? (() => new Audio()),
    options.acquireContext ?? (() => audioContext()),
    options.createStreamer ??
      ((context, onLevel, outputElement, onSpectrum) =>
        new AudioStreamer(context, onLevel, outputElement, onSpectrum)),
    options.onSpectrum,
  );
}
